import { useEffect, useState } from "react";

import proxy from "api/proxy";
import { fetchSiteData } from "./hotelDefaults";
import {
  HOTEL_SHEET_FIELDS,
  HOTEL_TEXT_FIELDS,
  HOTEL_FORM_TIME_FIELDS,
  MELHOUS_MODEL,
} from "../constants";

const FORM_FIELDS = [...HOTEL_TEXT_FIELDS, ...HOTEL_FORM_TIME_FIELDS];

function toTimeInput(value) {
  return value ? String(value).slice(0, 5) : "";
}

function toForm(melhous) {
  const form = {};
  HOTEL_TEXT_FIELDS.forEach((name) => {
    form[name] = melhous[name] ?? "";
  });
  HOTEL_FORM_TIME_FIELDS.forEach((name) => {
    form[name] = toTimeInput(melhous[name]);
  });
  return form;
}

function toTimeDefaults(defaults) {
  const result = { ...defaults };
  HOTEL_FORM_TIME_FIELDS.forEach((name) => {
    if (result[name]) result[name] = toTimeInput(result[name]);
  });
  return result;
}

function toSaveValue(name, value) {
  if (!value) return null;
  return HOTEL_FORM_TIME_FIELDS.includes(name) ? `${value}:00` : value;
}

function getChanges(form, saved) {
  const changes = {};
  FORM_FIELDS.forEach((name) => {
    if (form[name] !== saved[name]) changes[name] = toSaveValue(name, form[name]);
  });
  return changes;
}

function useHotelForm() {
  const [site, setSite] = useState(null);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [asideKey, setAsideKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await fetchSiteData(HOTEL_SHEET_FIELDS);
        if (cancelled) return;
        setSite(result);
        setFailed(!result.melhous);
        if (result.melhous) {
          const initial = toForm(result.melhous);
          setForm(initial);
          setSaved(initial);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleChange(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function saveHotel() {
    const changes = getChanges(form, saved);
    if (!Object.keys(changes).length) return;
    setSaving(true);
    setSaveError(false);
    const res = await proxy.saveQuery({
      model: MELHOUS_MODEL,
      storeRec: { id: site.melhous.id, ...changes },
      fieldsNames: ["id"],
    });
    setSaving(false);
    if (!res || res.error) {
      setSaveError(true);
      return;
    }
    setSaved(form);
    setShowAlert(true);
    setAsideKey((key) => key + 1);
  }

  const dirty = form && saved && Object.keys(getChanges(form, saved)).length > 0;

  return {
    site,
    form,
    failed,
    saving,
    showAlert,
    saveError,
    asideKey,
    dirty,
    defaults: site ? toTimeDefaults(site.defaults) : {},
    handleChange,
    saveHotel,
    closeAlert: () => setShowAlert(false),
  };
}

export default useHotelForm;
